import React from "react";
import PropTypes from "prop-types";

// @material-ui/core components
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import CustomReactSelect from "components/CustomDropdown/CustomReactSelect";

// styles and hooks
import { grayColor } from "../../assets/jss/material-dashboard-pro-react";
import useThemeColors from "../../hooks/useThemeColors";

const NewFilterDialog = ({
  open,
  onClose,
  tempNewFilters,
  setTempNewFilters,
  newFilters,
  setNewFilters,
  stages,
  tags,
  leaders,
  classes,
  t,
}) => {
  const { clientColor, clientTextColor } = useThemeColors();

  const handleClearAll = () => {
    const clearedFilters = {
      stage: null,
      leader: null,
      tags: null,
    };
    setTempNewFilters(clearedFilters);
    setNewFilters(clearedFilters);
    onClose();
  };

  const handleApply = () => {
    setNewFilters(tempNewFilters);
    onClose();
  };

  const handleCancel = () => {
    setTempNewFilters(newFilters);
    onClose();
  };

  // Custom styles for select components with floating labels
  const selectCustomStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "0",
      borderWidth: "0 0 1px 0",
      borderColor: state.isFocused ? clientColor : grayColor[4],
      marginTop: "0",
      boxShadow: "none",
      minHeight: "35px",
      height: "35px", // Fixed height to prevent layout shifts
      "&:hover": {
        borderColor: clientColor,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      padding: "6px 12px",
      color: state.isSelected ? clientTextColor : grayColor[14], // clientTextColor for selected options over clientColor
      backgroundColor: state.isSelected
        ? clientColor
        : state.isFocused
          ? grayColor[13]
          : "white",
      "&:active": {
        backgroundColor: clientColor,
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: "2px",
      zIndex: "999999",
      boxShadow: "0 2px 5px 0 rgba(0, 0, 0, 0.26)",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: "999999",
    }),
    singleValue: (provided) => ({
      ...provided,
      marginTop: "4px",
      color: grayColor[14], // Reverted back to original color
      fontSize: "14px",
      fontWeight: "300",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: grayColor[7],
      fontSize: "13px",
      fontStyle: "italic",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: grayColor[7],
      cursor: "pointer",
      "&:hover": {
        color: grayColor[14],
      },
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      "& > div:last-child": {
        display: "flex", // Always show the dropdown indicator
      },
    }),
    separator: () => ({
      display: "none",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      PaperProps={{
        style: {
          overflow: "visible",
        },
      }}
    >
      <ModalHeader
        title={t("initiatives.filters")}
        subtitle={t("initiatives.filter_description")}
        onClose={onClose}
        closeLabel={t("initiatives.close")}
        clientColor={clientColor}
      />

      <DialogContent
        className={classes.modalBody}
        style={{ overflow: "visible" }}
      >
        <GridContainer style={{ marginTop: "5px" }}>
          {/* Stage Filter */}
          <GridItem xs={12}>
            <div
              style={{
                width: "100%",
                marginBottom: "24px",
              }}
            >
              <CustomReactSelect
                title={t("initiatives.stage")}
                isClearable
                placeholder=""
                options={stages.map((stage) => ({
                  value: stage,
                  label: t(`initiatives.stages.${stage}`),
                }))}
                value={
                  tempNewFilters.stage
                    ? {
                        value: tempNewFilters.stage,
                        label: t(`initiatives.stages.${tempNewFilters.stage}`),
                      }
                    : null
                }
                onChange={(selected) =>
                  setTempNewFilters((draft) => {
                    draft.stage = selected ? selected.value : null;
                  })
                }
                customStyles={selectCustomStyles}
                baseColor={clientColor}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </GridItem>

          {/* Leader Filter */}
          <GridItem xs={12}>
            <div
              style={{
                width: "100%",
                marginBottom: "24px",
              }}
            >
              <CustomReactSelect
                title={t("initiatives.leader")}
                isClearable
                placeholder=""
                options={leaders.map((leader) => ({
                  value: leader,
                  label: leader,
                }))}
                value={
                  tempNewFilters.leader
                    ? {
                        value: tempNewFilters.leader,
                        label: tempNewFilters.leader,
                      }
                    : null
                }
                onChange={(selected) =>
                  setTempNewFilters((draft) => {
                    draft.leader = selected ? selected.value : null;
                  })
                }
                customStyles={selectCustomStyles}
                baseColor={clientColor}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </GridItem>

          {/* Tags Filter */}
          <GridItem xs={12}>
            <div
              style={{
                width: "100%",
                marginBottom: "24px",
              }}
            >
              <CustomReactSelect
                title={t("initiatives.tags")}
                isClearable
                placeholder=""
                options={tags.map((tag) => ({
                  value: tag.id,
                  label: tag.name,
                }))}
                value={
                  tempNewFilters.tags
                    ? {
                        value: tempNewFilters.tags,
                        label:
                          tags.find((t) => t.id === tempNewFilters.tags)
                            ?.name || "",
                      }
                    : null
                }
                onChange={(selected) =>
                  setTempNewFilters((draft) => {
                    draft.tags = selected ? selected.value : null;
                  })
                }
                customStyles={selectCustomStyles}
                baseColor={clientColor}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </GridItem>
        </GridContainer>
      </DialogContent>

      <DialogActions className={classes.modalFooter}>
        <Button
          color="transparent"
          simple
          onClick={handleClearAll}
          disabled={false}
        >
          {t("initiatives.clear_all")}
        </Button>
        <Button
          color="transparent"
          simple
          onClick={handleCancel}
          disabled={false}
        >
          {t("initiatives.cancel")}
        </Button>
        <Button color="client" simple onClick={handleApply} disabled={false}>
          {t("initiatives.apply_filters")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewFilterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tempNewFilters: PropTypes.object.isRequired,
  setTempNewFilters: PropTypes.func.isRequired,
  newFilters: PropTypes.object.isRequired,
  setNewFilters: PropTypes.func.isRequired,
  stages: PropTypes.array.isRequired,
  tags: PropTypes.array.isRequired,
  leaders: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default NewFilterDialog;
